import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { FileTextIcon, MailIcon, PrinterIcon, RotateCcwIcon, UserIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { fetchOrder } from '../adminApi';
import { useAdmin } from '../AdminContext';
import { PageHeader } from '../ui/PageHeader';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { MissingItem } from '../ui/MissingItem';
import { LoadFailed } from '../ui/LoadState';
import { Spinner } from '../../ui/Spinner';
import { btnDestructive, btnSecondary } from '../ui/styles';
import { paymentLabel, received } from '../inbox/inboxRules';
import { RefundForm } from './RefundForm';
import { CancelForm } from './CancelForm';
import { OrderTimeline } from './OrderTimeline';
import { FulfilmentPanel } from './FulfilmentPanel';
import { creditNumber, printCreditNote, printOrderInvoice, printOrderPackingSlip } from './orderDocuments';
import { channelLabel, orderBadge, orderNumber, vatBreakdown } from './orderLabels';
import { money } from '../../core/format/currency';
import { cancelProblem, paymentMethodLabel, refundableCents } from '../../core/rules/revenue';
import { formatStreet } from '../../core/rules/contact';
import { trackingUrl } from '../../core/rules/tracking';
import { dayShort } from '../../core/format/dates';
import type { AdminOrder } from '../../core/types/adminRequests';
import type { RequestOptions } from '../../core/types/backend';
type Action = 'refund' | 'cancel' | null;

/**
 * Eén bestelling: wat erin zat (met de btw zoals verkocht), het adres, de betaling, de tijdlijn en
 * de terugbetalingen. Terugbetalen en annuleren wachten altijd op de server (transactiescherm).
 */
export function OrderDetail() {
  const {
    id = ''
  } = useParams();
  // Vanuit de lijst: terug met dezelfde filters. Vanuit de inbox: terug naar de inbox.
  const {
    state
  } = useLocation() as {
    state: {
      from?: string;
      back?: {
        to: string;
        label: string;
      };
    } | null;
  };
  const back = state?.back ?? {
    to: `/be/bestellingen${state?.from ?? ''}`,
    label: 'Alle bestellingen'
  };
  const load = useCallback((opts: RequestOptions) => fetchOrder(id, opts), [id]);
  const {
    data: order,
    setData,
    status,
    reload,
    refresh
  } = useLatestLoad(load);
  const [action, setAction] = useState<Action>(null);
  if (status === 'loading' && !order) return <div role="status" className="flex items-center gap-2 py-16 text-[14px] text-muted" data-id="element-3371" data-mp="23ls056wxgwqv">
        <Spinner data-id="element-3372" data-mp="1yk2llhahy0y1" /> Bestelling laden…
      </div>;
  if (status === 'error' && !order) return <LoadFailed title="Deze bestelling kon niet laden" onRetry={reload} data-id="element-3373" data-mp="1tmkkue6mus14" />;
  if (!order) return <MissingItem title="Bestelling niet gevonden" back={back} data-id="element-3374" data-mp="1pmijk64t80md" />;
  const done = (next: AdminOrder, message: string) => {
    setData(next);
    setAction(null);
    toast.success(message);
  };
  return <OrderView order={order} back={back} action={action} setAction={setAction} onRefunded={next => done(next, 'Terugbetaald. De klant krijgt een mail van Mollie.')} onCancelled={next => done(next, 'Bestelling geannuleerd')} onFailed={refresh} onFulfilled={refresh} data-id="element-3375" data-mp="3jzi2o98efcye" />;
}
type ViewProps = {
  order: AdminOrder;
  back: {
    to: string;
    label: string;
  };
  action: Action;
  setAction: (a: Action) => void;
  onRefunded: (o: AdminOrder) => void;
  onCancelled: (o: AdminOrder) => void;
  onFailed: () => void;
  onFulfilled: () => void;
};
function OrderView({
  order: o,
  back,
  action,
  setAction,
  onRefunded,
  onCancelled,
  onFailed,
  onFulfilled
}: ViewProps) {
  const {
    site,
    index
  } = useAdmin();
  const badge = orderBadge(o);
  const paid = o.paymentStatus === 'betaald';
  const left = refundableCents(o);
  const canRefund = paid && left > 0 && !o.cancelledAt;
  const canCancel = cancelProblem(o) === null;
  const vat = vatBreakdown(o);
  const a = o.address;

  // Het formulier opent onder de regels; op de telefoon valt dat buiten beeld.
  const formRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (action) formRef.current?.scrollIntoView({
      block: 'nearest'
    });
  }, [action]);
  const invoice = async () => {
    if (!(await printOrderInvoice(o, site.salon, dayShort(o.placedAt.slice(0, 10))))) toast.error('Factuur openen lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  const slip = async () => {
    if (!(await printOrderPackingSlip(o, index.productById, site.salon))) toast.error('Pakbon afdrukken lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  return <div data-id="element-3376" data-mp="33gco2lfp6ucl">
      <PageHeader title={o.number ? `Bestelling ${o.number}` : 'Bestelling zonder nummer'} description={`${o.customerName || o.email} · ${received(o.placedAt)} · ${channelLabel[o.channel]} · ${money(o.totalCents)}`} back={back} actions={<>
            <Badge tone={badge.tone} data-id="element-3378" data-mp="37eae1qhalkh1">{badge.label}</Badge>
            {paid && <button type="button" onClick={() => void invoice()} className={btnSecondary} data-id="element-3379" data-mp="3r2q4fbf2pr9p">
                <FileTextIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3380" data-mp="3w08ckzl9o2gn" />
                Factuur
              </button>}
            {paid && !o.cancelledAt && <button type="button" onClick={() => void slip()} className={btnSecondary} data-id="element-3381" data-mp="3m192dckuryrj">
                <PrinterIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3382" data-mp="38anjhnvefotn" />
                Pakbon
              </button>}
          </>} data-id="element-3377" data-mp="2yfgj04jm125l" />

      <div className="grid gap-6 lg:grid-cols-3" data-id="element-3383" data-mp="2ye0y8xz5uvr3">
        <div className="space-y-6 lg:col-span-2" data-id="element-3384" data-mp="2yb6el8nz1eyq">
          <FulfilmentPanel order={o} onChanged={onFulfilled} data-id="element-3385" data-mp="33bkj4gqd66s0" />
          <Panel title="Wat er besteld is" flush data-id="element-3386" data-mp="2pckwtfqbq3z0">
            <table className="w-full text-[14px]" data-id="element-3387" data-mp="2pdvzfc8195oj">
              <caption className="sr-only" data-id="element-3388" data-mp="2ubo1gz2e686g">Bestelregels met btw</caption>
              <thead className="text-[12px] text-muted" data-id="element-3389" data-mp="2udqrhj3jo1b6">
                <tr className="border-b border-line" data-id="element-3390" data-mp="zwhal2yto019">
                  <th scope="col" className="px-4 py-2 text-left font-normal sm:px-5" data-id="element-3391" data-mp="zxab3m76ttay">Product</th>
                  <th scope="col" className="px-2 py-2 text-right font-normal" data-id="element-3392" data-mp="v2w74vegmduo">Aantal</th>
                  <th scope="col" className="hidden px-2 py-2 text-right font-normal sm:table-cell" data-id="element-3393" data-mp="q3jpqq6cxyzh">Btw</th>
                  <th scope="col" className="px-4 py-2 text-right font-normal sm:px-5" data-id="element-3394" data-mp="m27vg7cjgpky">Bedrag</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line" data-id="element-3395" data-mp="1duyv6rtfkwey">
                {o.lines.map(l => <tr key={l.productId} data-id="element-3396" data-mp="19su3hymjm9uy">
                    <td className="px-4 py-3 text-ink sm:px-5" data-id="element-3397" data-mp="19rsgcvs95wwv">
                      {l.name}
                      <span className="block text-[12px] text-muted sm:hidden" data-id="element-3398" data-mp="14u923vvk1k4x">btw {l.vatRate}%</span>
                    </td>
                    <td className="px-2 py-3 text-right tabular-nums" data-id="element-3399" data-mp="3uhhvl9wvbkq3">{l.qty}</td>
                    <td className="hidden px-2 py-3 text-right tabular-nums text-muted sm:table-cell" data-id="element-3400" data-mp="3pmucjqkmviiw">{l.vatRate}%</td>
                    <td className="px-4 py-3 text-right tabular-nums text-ink sm:px-5" data-id="element-3401" data-mp="2a1th996vpw2x">{money(l.lineTotalCents)}</td>
                  </tr>)}
              </tbody>
            </table>
            <dl className="space-y-1.5 border-t border-line px-4 py-4 text-[13px] sm:px-5" data-id="element-3402" data-mp="2iu3rurlos3kp">
              <div className="flex justify-between gap-4" data-id="element-3403" data-mp="2ivyfwes98bt0">
                <dt className="text-muted" data-id="element-3404" data-mp="2nsf2v5npwk4i">Verzending (btw {o.shippingVatRate}%)</dt>
                <dd className="tabular-nums text-ink" data-id="element-3405" data-mp="2sr8gcy308b4o">{o.shippingCents ? money(o.shippingCents) : 'Gratis'}</dd>
              </div>
              {vat.map(v => <div key={v.vatRate} className="flex justify-between gap-4" data-id="element-3406" data-mp="31szijsyc5zts">
                  <dt className="text-muted" data-id="element-3407" data-mp="31qvhdirtnex7">Waarvan btw {v.vatRate}%</dt>
                  <dd className="tabular-nums text-muted" data-id="element-3408" data-mp="13exy93h9s1d5">{money(v.vatCents)}</dd>
                </div>)}
              <div className="flex justify-between gap-4 pt-1.5 text-[15px]" data-id="element-3409" data-mp="17h1avvfodc00">
                <dt className="text-ink" data-id="element-3410" data-mp="oh57f2s1abz2">Totaal</dt>
                <dd className="tabular-nums text-ink" data-id="element-3411" data-mp="jjv88veuely1">{money(o.totalCents)}</dd>
              </div>
              {o.voucherCents > 0 && <div className="flex justify-between gap-4" data-id="element-3412" data-mp="x9qe3kie4s7j">
                  <dt className="text-muted" data-id="element-3413" data-mp="t9ohvqvfcqzn">Betaald met cadeaubon (gaat niet via Mollie terug)</dt>
                  <dd className="shrink-0 tabular-nums text-ink" data-id="element-3414" data-mp="5jkla3gnlri5">{money(o.voucherCents)}</dd>
                </div>}
              {o.refunds.map(r => <div key={r.id} className="flex justify-between gap-4" data-id="element-3415" data-mp="mu051pzs5pv">
                  <dt className="min-w-0 text-muted" data-id="element-3416" data-mp="em3jga9hvfy2">
                    Terugbetaald {received(r.at)} · <span className="text-ink" data-id="element-3417" data-mp="9knwudro51dk">{r.reason}</span>
                    {r.restocked && <span data-id="element-3418" data-mp="1pyw8veetbsay"> · terug op voorraad</span>}
                    {r.voucherRestoredCents ? <span data-id="element-3419" data-mp="1l0uomq5nhmxw"> · {money(r.voucherRestoredCents)} terug op cadeaubon</span> : null}
                    <button type="button" onClick={() => void printCreditNote(o, r, site.salon).then(ok => {
                  if (!ok) toast.error('Creditnota openen lukte niet', {
                    description: 'Probeer het opnieuw of gebruik een andere browser.'
                  });
                })} className="ml-2 text-ink underline decoration-line underline-offset-4 hover:decoration-ink" data-id="element-3420" data-mp="1thmq1chikixa">
                      Creditnota {creditNumber(o, r)}
                    </button>
                  </dt>
                  <dd className="shrink-0 tabular-nums text-clay" data-id="element-3421" data-mp="22j2yheorqayv">−{money(r.amountCents)}</dd>
                </div>)}
            </dl>
            {(o.gift || o.code) && <p className="border-t border-line px-4 py-3 text-[13px] text-muted sm:px-5" data-id="element-3422" data-mp="2v78iu0bwtxy6">
                {o.gift && <>
                    Cadeau erbij: <span className="text-ink" data-id="element-3423" data-mp="2zz03xvcarbgx">{o.gift}</span>
                  </>}
                {o.gift && o.code && ' · '}
                {o.code && <>
                    Code: <span className="text-ink" data-id="element-3424" data-mp="1wyhizx1rowyc">{o.code}</span>
                  </>}
              </p>}
          </Panel>

          <div ref={formRef} className="scroll-mb-24" data-id="element-3425" data-mp="1nz49539qe6d3">
            {action === 'refund' ? <RefundForm order={o} onDone={onRefunded} onFailed={onFailed} onClose={() => setAction(null)} data-id="element-3426" data-mp="19ahi44aqrq61" /> : action === 'cancel' ? <CancelForm order={o} onDone={onCancelled} onFailed={onFailed} onClose={() => setAction(null)} data-id="element-3427" data-mp="1e30yta4q71f2" /> : (canRefund || canCancel) && <div className="flex flex-wrap items-center gap-2" data-id="element-3428" data-mp="14cptbj5e8k62">
                  {canRefund && <button type="button" onClick={() => setAction('refund')} className={btnSecondary} data-id="element-3429" data-mp="qcnde2gnc0tg">
                      <RotateCcwIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3430" data-mp="vaoy0rtq0871" />
                      Terugbetalen
                    </button>}
                  {canCancel && <button type="button" onClick={() => setAction('cancel')} className={`${btnDestructive} sm:ml-auto`} data-id="element-3431" data-mp="6sg4ya8dn8f2">
                      <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3432" data-mp="goj5jbnkyyzw" />
                      Bestelling annuleren
                    </button>}
                </div>}
          </div>
          {!canCancel && !o.cancelledAt && paid && o.fulfilment !== 'nieuw' && action === null && <p className="-mt-3 text-[12px] text-muted" data-id="element-3433" data-mp="14cslvy20yujn">Al ingepakt of verzonden: annuleren kan niet meer, terugbetalen wel.</p>}
        </div>

        <div className="space-y-6" data-id="element-3434" data-mp="1des4uvwlo8p8">
          <Panel title="Klant" data-id="element-3435" data-mp="1dftsdzusynnu">
            <p className="text-[14px] text-ink" data-id="element-3436" data-mp="1df1uujhnafux">{o.customerName || '—'}</p>
            <p className="break-all text-[13px] text-muted" data-id="element-3437" data-mp="19ehxpfx2nc8s">{o.email}</p>
            <address className="mt-3 text-[13px] not-italic leading-snug text-ink" data-id="element-3438" data-mp="1ggwvfow69f5n">
              {`${a.firstName} ${a.lastName}`.trim() || o.customerName}
              <br data-id="element-3439" data-mp="1lddlwpqug5lw" />
              {formatStreet(a)}
              <br data-id="element-3440" data-mp="1qeb4h3frn1nx" />
              {a.postcode} {a.city}
              {a.country === 'BE' && ' · België'}
            </address>
            {o.skinNote && <p className="mt-3 rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-3441" data-mp="1v748tq7bo7zh">
                <span className="block text-[12px] text-muted" data-id="element-3442" data-mp="1z8z8yrdiofbo">Notitie voor jou</span>“{o.skinNote}”
              </p>}
            <div className="mt-4 flex flex-wrap gap-2" data-id="element-3443" data-mp="295c0pjpi4j3s">
              {o.customerId ? <Link to={`/be/klanten/${o.customerId}?tab=bestellingen`} className={btnSecondary} data-id="element-3444" data-mp="2e3vlvhw9tqv3">
                  <UserIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3445" data-mp="eraz4r2khi00" />
                  Dossier
                </Link> : <span className="text-[12px] text-muted" data-id="element-3446" data-mp="jqe6wj9xm2bo">Gastbestelling</span>}
              <a href={`mailto:${o.email}?subject=${encodeURIComponent(`Je bestelling ${orderNumber(o)}`)}`} className={btnSecondary} data-id="element-3447" data-mp="2x1yr9jnd7ezn">
                <MailIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3448" data-mp="2o83m4bm8tok0" />
                Mail
              </a>
            </div>
          </Panel>

          <Panel title="Betaling" data-id="element-3449" data-mp="2jak9th93vlup">
            <dl className="space-y-1.5 text-[13px]" data-id="element-3450" data-mp="2j7690dg3bkpd">
              <div className="flex justify-between gap-4" data-id="element-3451" data-mp="3fxyp3b30xcr5">
                <dt className="text-muted" data-id="element-3452" data-mp="3bxxzqvedzh5b">Status</dt>
                <dd className="text-ink" data-id="element-3453" data-mp="3by6fcv1w90k7">{paymentLabel[o.paymentStatus]}</dd>
              </div>
              <div className="flex justify-between gap-4" data-id="element-3454" data-mp="36x0icl17ar4v">
                <dt className="text-muted" data-id="element-3455" data-mp="3200caa4cpodr">Methode</dt>
                <dd className="text-ink" data-id="element-3456" data-mp="321l780ukv9c5">{paymentMethodLabel[o.paymentMethod]}</dd>
              </div>
              {o.refundedCents > 0 && <div className="flex justify-between gap-4" data-id="element-3457" data-mp="1qjse0kvycz7w">
                  <dt className="text-muted" data-id="element-3458" data-mp="1qmof2i33k40k">Nog terug te betalen</dt>
                  <dd className="tabular-nums text-ink" data-id="element-3459" data-mp="3efgka367mg4e">{money(left)}</dd>
                </div>}
              {o.tracking && <div className="flex justify-between gap-4" data-id="element-3460" data-mp="35gbo33i4eyve">
                  <dt className="text-muted" data-id="element-3461" data-mp="35hwdyfthptmx">Track & trace</dt>
                  <dd data-id="element-3462" data-mp="3aemwrawoj3c1">
                    <a href={trackingUrl(o.tracking)} target="_blank" rel="noopener noreferrer" className="text-ink underline underline-offset-2" data-id="element-3463" data-mp="3ag7nepfvi24m">
                      {o.tracking.carrier} {o.tracking.code}
                      <span className="sr-only" data-id="element-3464" data-mp="60ohk0ka458r"> (opent in een nieuw tabblad)</span>
                    </a>
                  </dd>
                </div>}
            </dl>
          </Panel>

          <Panel title="Tijdlijn" data-id="element-3465" data-mp="3t4crxz4ctr4h">
            <OrderTimeline events={o.timeline} data-id="element-3466" data-mp="2crnwyraoxf9x" />
          </Panel>
        </div>
      </div>
    </div>;
}